import React, {FC} from 'react';

import {Box} from '@mui/material';
import dayjs from 'dayjs';
import CustomParseFormat from 'dayjs/plugin/customParseFormat';
import {useTranslation} from 'react-i18next';

import {usePromotionRibbon} from 'hooks/recommendation-hooks/usePromotionRibbon';
import {ComplexPromotionType, determinePromotionType} from 'utils/recommendation-utils/proPointsPromotion.util';

import {StyledRibbonBox} from './styles';
import CombosetIcon from '../../assets/icons/combosetIcon';
import {config} from '../../config/env';
import theme from '../../config/theme';
import {useAppSelector} from '../../hooks';
import SendBrowserEvent from '../../services/send-browser-event';
import {BasketLineItem} from '../../types';
import {DeviceType, getDeviceType, isFeatureEnabled} from '../../utils';
import {Footnote} from '../basket-modals/styles';
import {StyledCardContent, StyledCardContentUnpadded} from '../basket-product/styles';

dayjs.extend(CustomParseFormat);

const ComplexPromotionTag: FC<{basketItem: BasketLineItem}> = ({basketItem}) => {
  const isComplexPromotionApplied = Boolean(basketItem?.productDetails?.promotion?.complexPromotionIds.length);
  const mininumVersion = config.IS_COMPLEX_PROMOTION_ENABLED;
  const promotionRibbon = usePromotionRibbon(basketItem.productDetails);

  const appVersion = useAppSelector((state) => state.app.appVersion);
  const isComplexPromotionEnabled = isFeatureEnabled(mininumVersion, appVersion || '1.0.0');

  const {t} = useTranslation();
  const {rm3Promotions, basket} = useAppSelector((state) => state?.basket);
  const skuPromotion = rm3Promotions[basketItem.productDetails.sku];
  const isBogo = promotionRibbon.type === ComplexPromotionType.BOGO;
  const isBogoAndShouldShowAddMore = isBogo && basketItem.quantity % 2 === 1;

  // filter sku promotion that exist id advancedPromotions
  const countPromotion = (skuPromotion?.promoAdvice ?? []).filter((sku) =>
    (basket?.advancedPromotions ?? []).find((promo) => Number(promo.complexPromotionId) === Number(sku.promoId))
  );

  const getEndDateText = (day: number) => {
    if (day < 1) {
      return t('proPointPromotion.endingToday');
    }
    if (day === 1) {
      return t('proPointPromotion.endingTMR');
    }
    return t('proPointPromotion.ending', {
      day: day
    });
  };
  const openPromotionDetailModal = () => {
    if (skuPromotion) {
      const startDate = dayjs().startOf('day');

      const selectedPromotion = countPromotion.map((p) => {
        const promotionType = determinePromotionType(
          true,
          basketItem.productDetails.displayPrice,
          p.meta.rewardValue,
          p.meta.targetValue,
          p.subType
        );
        const endDate = dayjs(p.endDate, 'DD-MM-YYYY').startOf('day');

        const daysLeft = endDate.diff(startDate, 'days');

        return {
          ...p,
          title: promotionType === ComplexPromotionType.BOGO ? t('complexPromotions.bogoPromotion') : p.title,
          isShowEndingDays: daysLeft < config.COMPLEX_PROMOTION_MINIMUM_DATE,
          endingDaysTxt: getEndDateText(daysLeft)
        };
      });

      SendBrowserEvent.openComplextPromotionDetailModal(selectedPromotion, config.COMPLEX_PROMOTION_MINIMUM_DATE);
    }
  };
  if (!isComplexPromotionEnabled || getDeviceType() === DeviceType.DESKTOP) {
    return null;
  }

  if (isBogoAndShouldShowAddMore) {
    return (
      <StyledCardContentUnpadded>
        <StyledRibbonBox>
          <CombosetIcon />
          <Footnote display={'block'} variant="caption" color={theme.palette.common.mds_global_color_purple}>
            {t('complexPromotions.bogoPromotionAddMore')}
          </Footnote>
        </StyledRibbonBox>
      </StyledCardContentUnpadded>
    );
  }

  if (!(isComplexPromotionApplied && countPromotion?.length)) {
    return null;
  }

  return (
    <StyledCardContent
      onClick={() => {
        openPromotionDetailModal();
      }}
    >
      <Box display="flex" alignItems={'center'} gap={0.5}>
        <CombosetIcon />
        <Footnote
          display={'block'}
          variant="caption"
          style={{textDecoration: 'underline'}}
          color={theme.palette.common.mds_global_color_purple}
        >
          {t('proPointPromotion.applied', {
            promo: countPromotion?.length
          })}
        </Footnote>
      </Box>
    </StyledCardContent>
  );
};

export default React.memo(ComplexPromotionTag);
